import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Zap } from "lucide-react";
import { ForceLogo } from "./ForceLogo";

const DISMISS_KEY = "force_install_dismissed";

export default function InstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const standalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;
        if (standalone) return;

        function onBeforeInstall(e) {
            e.preventDefault();
            setDeferred(e);
            if (sessionStorage.getItem(DISMISS_KEY) !== "1") setVisible(true);
        }
        function onInstalled() {
            setVisible(false);
            setDeferred(null);
        }
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    async function install() {
        if (!deferred) return;
        deferred.prompt();
        try {
            await deferred.userChoice;
        } catch (e) {
            /* ignore */
        }
        setVisible(false);
        setDeferred(null);
    }

    function dismiss() {
        sessionStorage.setItem(DISMISS_KEY, "1");
        setVisible(false);
    }

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    data-testid="install-prompt"
                    initial={{ opacity: 0, y: 60 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 60 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="fixed bottom-4 inset-x-3 z-[100] mx-auto w-auto max-w-md"
                >
                    <div className="glass p-4 flex items-center gap-3">
                        <div className="absolute top-0 left-0 right-0 h-px shimmer" />
                        <ForceLogo size={32} className="shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-white leading-tight">
                                Install FORCE
                            </p>
                            <p className="text-[11px] text-zinc-400 leading-snug">
                                Add to your home screen — auto-updates, no reinstalling.
                            </p>
                        </div>
                        <button
                            data-testid="install-now-btn"
                            onClick={install}
                            className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-3.5 py-2.5 text-sm font-bold text-zinc-950 shadow-[0_6px_20px_rgba(255,122,0,0.4)] hover:brightness-110 transition-[filter] shrink-0"
                        >
                            <Download className="h-4 w-4" /> Install
                        </button>
                        <button
                            data-testid="install-dismiss-btn"
                            onClick={dismiss}
                            className="text-zinc-500 hover:text-white transition-colors shrink-0"
                            aria-label="Dismiss"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
